import { createContext, use } from 'react';

import type { TileExportEntry } from '#core/contexts/DashboardExportContext.ts';
import { type KeyedRegistry, useRegisterWithGetter } from '#core/hooks/useKeyedRegistry.ts';

/**
 * One dashboard's contribution to a message-level export. A message can
 * hold multiple `<DashboardSpecRenderer>`s (multiple `showDashboard` tool
 * invocations); each registers itself here with a snapshot getter that
 * returns its current tile entries. The action-bar download button calls
 * every `getEntries()` and bundles the union into one ZIP, namespaced by
 * `name`.
 */
export interface MessageDashboardEntry {
  readonly id: string;
  readonly name: string;
  readonly getEntries: () => readonly TileExportEntry[];
}

export const MessageExportContext = createContext<KeyedRegistry<MessageDashboardEntry> | null>(null);

/**
 * Register one dashboard's tile snapshot with the enclosing message. Pass
 * `null` outside a message scope — the hook stays in the render path
 * (rules of hooks) but does nothing.
 *
 * `getEntries` is shielded through `useRegisterWithGetter` so the registered
 * snapshot resolves to the latest list at click time without re-registering
 * when the dashboard's tile registry updates.
 */
export function useRegisterMessageDashboard(entry: MessageDashboardEntry | null): void {
  const ctx = use(MessageExportContext);
  useRegisterWithGetter(ctx, entry, 'getEntries', (e) => e.getEntries());
}
